/**
 * Gabarit par défaut — transcription du design imprimé exactement tel
 * qu'approuvé par la CNSS (bon à tirer SADEC/Evolis, contrat
 * CNSS/DG/CGPMP/F/03/2025, réf. REF-01/REF-02), avant l'introduction du
 * studio de design. Semé en base comme le gabarit `estDefautSysteme` (voir
 * `apps/api/prisma/seed.ts`) et utilisé par `CartePrintPreview` tant
 * qu'aucun gabarit n'a été publié par un administrateur — l'admin dispose
 * ainsi toujours d'un point de départ à dupliquer plutôt qu'une page
 * blanche. Coordonnées en mm, approximées à partir de l'ancien layout en
 * flexbox (`git show` sur `CarteTab.tsx` avant ce studio) : un admin peut
 * ensuite les affiner au pixel près dans le studio, ce gabarit n'a pas
 * besoin d'être millimétré à la perfection pour remplir son rôle de départ.
 *
 * Couleurs échantillonnées sur le bon à tirer (voir PROGRESS.md) :
 * bleu `#27408a`, rouge `#eb1741`, navy `#242659`.
 */
const CARTE_BLEU = '#27408a';
const CARTE_ROUGE = '#eb1741';
const CARTE_NAVY_VERSO = '#242659';
const NOIR = '#000000';
let idCounter = 0;
function nextId(prefix) {
    idCounter += 1;
    return `${prefix}-${idCounter}`;
}
function texte(partial) {
    return { id: nextId('texte'), type: 'texte', rotationDeg: 0, visibleSiAvecPuce: null, ...partial };
}
function image(partial) {
    return { id: nextId('image'), type: 'image', rotationDeg: 0, visibleSiAvecPuce: null, ...partial };
}
function builtin(name) {
    return { kind: 'builtin', name };
}
export const DEFAULT_CARTE_TEMPLATE_CONTENU = {
    recto: {
        elements: [
            image({
                x: 2.5,
                y: 2.95,
                width: 6,
                height: 4.2,
                zIndex: 1,
                opacity: 1,
                source: builtin('drapeau-rdc'),
                objectFit: 'cover',
                borderRadiusMm: 0.3,
            }),
            texte({
                x: 9.8,
                y: 1.8,
                width: 65.5,
                height: 2.2,
                zIndex: 1,
                opacity: 1,
                contenu: 'République Démocratique du Congo',
                fontFamily: 'Arial',
                fontSizePx: 3.6,
                fontWeight: 'bold',
                color: NOIR,
                align: 'center',
                transform: 'uppercase',
            }),
            texte({
                x: 9.8,
                y: 4.0,
                width: 65.5,
                height: 2.4,
                zIndex: 1,
                opacity: 1,
                contenu: 'Caisse Nationale de Sécurité Sociale',
                fontFamily: 'Arial',
                fontSizePx: 4.2,
                fontWeight: 'bold',
                color: CARTE_BLEU,
                align: 'center',
                transform: 'uppercase',
            }),
            texte({
                x: 9.8,
                y: 6.3,
                width: 65.5,
                height: 2.4,
                zIndex: 1,
                opacity: 1,
                contenu: 'Carte de Sécurité Sociale pour Travailleur',
                fontFamily: 'Arial',
                fontSizePx: 4.2,
                fontWeight: 'bold',
                color: CARTE_ROUGE,
                align: 'center',
                transform: 'uppercase',
            }),
            image({
                x: 76.6,
                y: 1.8,
                width: 6.5,
                height: 6.5,
                zIndex: 1,
                opacity: 1,
                source: builtin('cnss-icon'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            }),
            image({
                x: 30.8,
                y: 19.3,
                width: 24,
                height: 24,
                zIndex: 0,
                opacity: 0.16,
                visibleSiAvecPuce: true,
                source: builtin('cnss-icon'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            }),
            { id: nextId('photo'), type: 'photo_travailleur', x: 2.5, y: 9.8, width: 12, height: 15, zIndex: 2, opacity: 1, rotationDeg: 0, visibleSiAvecPuce: null },
            texte({
                x: 16.5,
                y: 9.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'NUMERO : {{numeroImmatriculation}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: CARTE_BLEU,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 16.5,
                y: 11.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'NOM : {{nom}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: NOIR,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 16.5,
                y: 13.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'POSTNOM : {{postnom}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: NOIR,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 16.5,
                y: 15.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'PRENOM : {{prenom}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: NOIR,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 16.5,
                y: 17.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'NE(E) LE : {{dateNaissance}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: NOIR,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 16.5,
                y: 19.8,
                width: 66.6,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'NATIONALITE : {{nationalite}}',
                fontFamily: 'Arial',
                fontSizePx: 4.4,
                fontWeight: 'bold',
                color: NOIR,
                align: 'left',
                transform: 'uppercase',
            }),
            texte({
                x: 2.5,
                y: 48.6,
                width: 45,
                height: 2.0,
                zIndex: 2,
                opacity: 1,
                contenu: '{{carte.numeroCarte}}',
                fontFamily: 'Arial',
                fontSizePx: 4.2,
                fontWeight: 'bold',
                color: CARTE_ROUGE,
                align: 'left',
                transform: 'none',
            }),
            texte({
                x: 2.5,
                y: 50.8,
                width: 50,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'Fait à KINSHASA, le {{carte.datePersonnalisation}}',
                fontFamily: 'Arial',
                fontSizePx: 3.4,
                fontWeight: 'normal',
                color: NOIR,
                align: 'left',
                transform: 'none',
            }),
            texte({
                x: 57,
                y: 43.8,
                width: 26,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'Le Directeur Général',
                fontFamily: 'Arial',
                fontSizePx: 3.6,
                fontWeight: 'bold',
                color: NOIR,
                align: 'center',
                transform: 'none',
            }),
            image({
                x: 62,
                y: 45.7,
                width: 16,
                height: 3.2,
                zIndex: 2,
                opacity: 1,
                source: builtin('signature-directeur-general'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            }),
            texte({
                x: 57,
                y: 49.1,
                width: 26,
                height: 1.8,
                zIndex: 2,
                opacity: 1,
                contenu: 'Charles MUDIAYI KAZADI',
                fontFamily: 'Arial',
                fontSizePx: 3.6,
                fontWeight: 'bold',
                color: NOIR,
                align: 'center',
                transform: 'none',
            }),
        ],
    },
    verso: {
        elements: [
            ...[8.55, 28.7, 48.85, 69.0].map((x) => image({
                x,
                y: 1.5,
                width: 8.06,
                height: 3,
                zIndex: 1,
                opacity: 1,
                source: builtin('cnss-icon-filigrane'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            })),
            { id: nextId('qrcode'), type: 'qrcode', x: 6, y: 15.5, width: 16, height: 16, zIndex: 2, opacity: 1, rotationDeg: 0, visibleSiAvecPuce: null },
            image({
                x: 24,
                y: 20.5,
                width: 5,
                height: 6,
                zIndex: 2,
                opacity: 1,
                visibleSiAvecPuce: true,
                source: builtin('puce-placeholder'),
                objectFit: 'contain',
                borderRadiusMm: 0.5,
            }),
            texte({
                x: 48,
                y: 20.5,
                width: 12,
                height: 3,
                zIndex: 2,
                opacity: 1,
                contenu: 'CNSS',
                fontFamily: 'Arial',
                fontSizePx: 7,
                fontWeight: 'bold',
                color: CARTE_NAVY_VERSO,
                align: 'right',
                transform: 'none',
            }),
            image({
                x: 61,
                y: 17,
                width: 7,
                height: 7,
                zIndex: 2,
                opacity: 1,
                source: builtin('cnss-icon'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            }),
            ...[8.55, 28.7, 48.85, 69.0].map((x) => image({
                x,
                y: 42.5,
                width: 8.06,
                height: 3,
                zIndex: 1,
                opacity: 1,
                source: builtin('cnss-icon-filigrane'),
                objectFit: 'contain',
                borderRadiusMm: 0,
            })),
            texte({
                x: 4,
                y: 45.7,
                width: 77.6,
                height: 3.4,
                zIndex: 2,
                opacity: 1,
                contenu: 'Cette carte certifie que le travailleur désigné au recto est immatriculé au régime général de la sécurité sociale.',
                fontFamily: 'Arial',
                fontSizePx: 3.2,
                fontWeight: 'normal',
                color: NOIR,
                align: 'center',
                transform: 'none',
            }),
            texte({
                x: 4,
                y: 49.3,
                width: 77.6,
                height: 3.4,
                zIndex: 2,
                opacity: 1,
                contenu: "Le travailleur est appelé à conserver cette carte et à la présenter à l'employeur lors d'un nouvel engagement.",
                fontFamily: 'Arial',
                fontSizePx: 3.2,
                fontWeight: 'normal',
                color: NOIR,
                align: 'center',
                transform: 'none',
            }),
        ],
    },
};
//# sourceMappingURL=default-carte-template.js.map